import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, MessageSquare, X } from 'lucide-react';
import { Button } from './ui/button';

const IncomingCallNotification = ({ notification, onDismiss }) => {
  const navigate = useNavigate();
  const [isRinging, setIsRinging] = useState(true);
  const audioContextRef = useRef(null);

  // Play ringtone when component mounts
  useEffect(() => {
    // Create audio context
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      console.warn('Web Audio API not supported');
      return;
    }
    
    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;
    
    const playRingtone = () => {
      if (!isRinging || audioContext.state === 'closed') return;
      
      try {
        // Resume audio context if suspended (required for autoplay policy)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
        
        // Create a more attention-grabbing ring pattern
        const playTone = (freq, startTime, duration) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = freq;
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.4, audioContext.currentTime + startTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
          
          oscillator.start(audioContext.currentTime + startTime);
          oscillator.stop(audioContext.currentTime + startTime + duration);
        };
        
        // Phone-like ring pattern: two-tone ring
        playTone(440, 0, 0.15);      // A4
        playTone(480, 0.15, 0.15);   // B4
        playTone(440, 0.35, 0.15);   // A4
        playTone(480, 0.5, 0.15);    // B4
      } catch (err) {
        console.warn('Audio playback error:', err);
      }
    };

    // Ring pattern: ring every 2 seconds
    const ringInterval = setInterval(() => {
      if (isRinging) {
        playRingtone();
      }
    }, 2000);

    // Initial ring
    playRingtone();

    // Auto-dismiss after 45 seconds
    const autoDismiss = setTimeout(() => {
      setIsRinging(false);
      onDismiss();
    }, 45000);

    return () => {
      clearInterval(ringInterval);
      clearTimeout(autoDismiss);
      setIsRinging(false);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [isRinging, onDismiss]);

  const handleAnswer = () => {
    setIsRinging(false);
    onDismiss();
    navigate('/communications');
  };

  const handleDismiss = () => {
    setIsRinging(false);
    onDismiss();
  };

  const isCall = notification?.event === 'incoming_call';
  const isSMS = notification?.event === 'incoming_sms';
  
  const data = notification?.data || {};
  const fromPhone = data.from_phone || 'Unknown';
  const contactName = data.contact_name || 'Unknown Caller';
  
  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11 && clean.startsWith('1')) {
      return `(${clean.slice(1, 4)}) ${clean.slice(4, 7)}-${clean.slice(7)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
    }
    return phone;
  };

  return (
    <>
      {/* Full screen overlay for calls to ensure visibility */}
      {isCall && (
        <div className="fixed inset-0 bg-black/30 z-[9998] animate-in fade-in duration-200" />
      )}
      
      {/* Notification popup */}
      <div className={`fixed z-[9999] animate-in slide-in-from-top-2 duration-300 ${
        isCall 
          ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' 
          : 'top-4 right-4'
      }`}>
        <div className={`rounded-2xl shadow-2xl overflow-hidden ${
          isCall ? 'w-96' : 'w-80'
        } ${
          isCall 
            ? 'bg-gradient-to-br from-green-500 to-green-700 ring-4 ring-green-300 ring-opacity-50' 
            : 'bg-gradient-to-br from-blue-500 to-blue-700'
        }`}>
          {/* Pulsing border for calls */}
          {isCall && (
            <div className="absolute inset-0 rounded-2xl animate-ping bg-green-400 opacity-20" />
          )}
          
          {/* Header */}
          <div className="relative p-5 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {isCall ? (
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center animate-pulse">
                    <Phone className="h-7 w-7 text-white" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                )}
                <div>
                  <p className={`font-bold ${isCall ? 'text-xl' : 'text-lg'}`}>
                    {isCall ? 'Incoming Call' : 'New Message'}
                  </p>
                  <p className="text-sm text-white/80">
                    {data.environment === 'production' ? 'Production Line' : 'Staging'}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-white/80" />
              </button>
            </div>
            
            {/* Caller/Sender Info */}
            <div className="text-center py-3">
              <p className={`font-bold ${isCall ? 'text-2xl' : 'text-xl'}`}>{contactName}</p>
              <p className="text-lg text-white/90">{formatPhone(fromPhone)}</p>
              {isSMS && data.body && (
                <p className="text-sm text-white/80 mt-3 line-clamp-2 italic bg-white/10 rounded-lg p-2">
                  "{data.body}"
                </p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`bg-white/10 backdrop-blur px-5 py-4 flex gap-3 ${isCall ? 'justify-center' : ''}`}>
            {isCall ? (
              <>
                <Button
                  onClick={handleDismiss}
                  size="lg"
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white border-0 h-14 text-lg"
                >
                  <PhoneOff className="h-5 w-5 mr-2" />
                  Dismiss
                </Button>
                <Button
                  onClick={handleAnswer}
                  size="lg"
                  className="flex-1 bg-white text-green-700 hover:bg-white/90 h-14 text-lg font-bold"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  View Call
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border-0"
                >
                  Dismiss
                </Button>
                <Button
                  onClick={handleAnswer}
                  className="flex-1 bg-white text-blue-700 hover:bg-white/90"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  View
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomingCallNotification;
